import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { invoiceService, commentService } from '../services/api';
import { Invoice, Comment } from '../types';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../hooks/useSocket';
import { ArrowLeft, Send, Edit2, Save, X, Clock, MessageSquare, Trash2, Building2, Phone, Mail, FileText, ChevronDown, User, Calendar } from 'lucide-react';

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { joinInvoice, leaveInvoice, onInvoiceRefresh, onNewComment, emitInvoiceUpdate, emitNewComment } = useSocket();
  
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Invoice>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [clientGestion, setClientGestion] = useState('');
  const [isAddingGestion, setIsAddingGestion] = useState(false);
  const [isSavingGestion, setIsSavingGestion] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchInvoice = useCallback(async () => {
    if (!id) return;
    try {
      const [invoiceRes, commentsRes] = await Promise.all([
        invoiceService.getById(parseInt(id)),
        commentService.getByInvoice(parseInt(id))
      ]);
      setInvoice(invoiceRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Error fetching invoice:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  useEffect(() => {
    if (id) {
      joinInvoice(parseInt(id));
      
      const unsubRefresh = onInvoiceRefresh(() => {
        fetchInvoice();
      });
      
      const unsubComment = onNewComment((comment: Comment) => {
        setComments(prev => {
          if (prev.some(c => c.id === comment.id)) {
            return prev;
          }
          return [comment, ...prev];
        });
      });

      return () => {
        leaveInvoice(parseInt(id));
        unsubRefresh?.();
        unsubComment?.();
      };
    }
  }, [id, joinInvoice, leaveInvoice, onInvoiceRefresh, onNewComment, fetchInvoice]);

  const handleSave = async () => {
    if (!invoice) return;
    setIsSaving(true);
    try {
      await invoiceService.update(invoice.id, editData);
      await fetchInvoice();
      emitInvoiceUpdate(invoice.id);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving invoice:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !invoice) return;
    try {
      const response = await commentService.create({
        invoiceId: invoice.id,
        comment: newComment
      });
      setComments(prev => [response.data, ...prev]);
      emitNewComment(invoice.id, response.data);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleSaveGestion = async () => {
    if (!invoice) return;
    setIsSavingGestion(true);
    try {
      const now = new Date();
      const dateTime = `${now.toLocaleDateString('en-US')} ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
      const newEntry = `[${dateTime} - ${userName}]: ${clientGestion}`;
      const newGestion = invoice.management_notes 
        ? `${invoice.management_notes}\n\n${newEntry}`
        : newEntry;
      
      await invoiceService.update(invoice.id, { managementNotes: newGestion });
      await fetchInvoice();
      emitInvoiceUpdate(invoice.id);
      setClientGestion('');
      setIsAddingGestion(false);
    } catch (error) {
      console.error('Error saving gestion:', error);
    } finally {
      setIsSavingGestion(false);
    }
  };

  const startEditing = () => {
    if (invoice) {
      setEditData({
        pending_notes: invoice.pending_notes,
        management_notes: invoice.management_notes,
        result: invoice.result,
        status: invoice.status,
        initials: invoice.initials
      });
      setIsEditing(true);
    }
  };

  const handleDelete = async () => {
    if (!invoice) return;
    
    if (!window.confirm(`¿Está seguro de eliminar la factura #${invoice.invoice_number}? Esta acción no se puede deshacer.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await invoiceService.delete(invoice.id);
      navigate('/invoices');
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Error al eliminar la factura');
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      in_process: 'bg-orange-100 text-orange-800 border-orange-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      denied: 'bg-red-100 text-red-800 border-red-300',
      paid: 'bg-blue-100 text-blue-800 border-blue-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Factura no encontrada</p>
        <button onClick={() => navigate('/invoices')} className="mt-4 text-blue-600 hover:underline">
          Volver a la lista
        </button>
      </div>
    );
  }

  const statusLabels: Record<string, string> = {
    pending: 'Pendiente',
    in_process: 'En Proceso',
    completed: 'Completada',
    denied: 'Denegada',
    paid: 'Pagada',
    refacturar: 'Re-Facturar',
  };

  return (
    <div className="flex gap-6 h-[calc(100vh-120px)]">
      {/* Left Panel - Main Content */}
      <div className="flex-1 flex flex-col bg-white rounded-xl shadow overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate(invoice?.client_id ? `/clients?selected=${invoice.client_id}` : '/clients')}
              className="p-1 hover:bg-gray-100 rounded transition"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-lg font-semibold text-gray-800">
              {invoice.pending_notes?.substring(0, 50) || `Factura #${invoice.invoice_number}`}
            </h1>
          </div>
          <p className="text-sm text-gray-500 ml-8">
            {invoice.company_name || `${invoice.client_first_name} ${invoice.client_last_name}`} &lt;{invoice.plan_code}&gt;
          </p>
        </div>

        {/* Gestión Thread */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {invoice.management_notes ? (
            invoice.management_notes.split('\n\n').map((entry, idx) => {
              const match = entry.match(/\[(.+?) - (.+?)\]: ([\s\S]*)/);
              if (match) {
                const [, dateTime, userName, content] = match;
                return (
                  <div key={idx} className="flex gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-800">{userName}</span>
                        <span className="text-xs text-gray-400">{dateTime}</span>
                      </div>
                      <p className="text-gray-700 text-sm">{content}</p>
                    </div>
                  </div>
                );
              }
              return (
                <div key={idx} className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
                  {entry}
                </div>
              );
            })
          ) : (
            <p className="text-gray-400 text-center py-8">Sin notas de gestión</p>
          )}
        </div>

        {/* Add Gestión / Reply */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          {isAddingGestion ? (
            <div className="space-y-3">
              <textarea
                value={clientGestion}
                onChange={(e) => setClientGestion(e.target.value)}
                placeholder="Escriba su gestión aquí..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={3}
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => { setIsAddingGestion(false); setClientGestion(''); }}
                  className="px-4 py-2 text-sm bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveGestion}
                  disabled={!clientGestion.trim() || isSavingGestion}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                >
                  {isSavingGestion ? 'Guardando...' : 'Enviar'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => setIsAddingGestion(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-white rounded-lg border border-gray-300"
              >
                <MessageSquare size={16} />
                Agregar nota
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Middle Panel - Properties */}
      <div className="w-72 bg-white rounded-xl shadow p-5 overflow-y-auto">
        <div className="mb-6">
          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invoice.status)}`}>
            {statusLabels[invoice.status]}
          </span>
          <p className="text-xs text-gray-400 mt-1">
            desde {new Date(invoice.created_at).toLocaleDateString('en-US')}
          </p>
        </div>

        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Propiedades</h3>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Plan</label>
            <p className="text-sm font-medium text-gray-800">{invoice.plan_code || '-'}</p>
          </div>

          <div>
            <label className="text-xs text-gray-500 block mb-1">Estado</label>
            <select
              value={invoice.status}
              onChange={async (e) => {
                try {
                  await invoiceService.update(invoice.id, { status: e.target.value });
                  await fetchInvoice();
                  emitInvoiceUpdate(invoice.id);
                } catch (error) {
                  console.error('Error updating status:', error);
                }
              }}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="pending">Pendiente</option>
              <option value="in_process">En Proceso</option>
              <option value="completed">Completada</option>
              <option value="denied">Denegada</option>
              <option value="refacturar">Re-Facturar</option>
              <option value="paid">Pagada</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-500 block mb-1">Monto</label>
            <p className="text-sm font-medium text-gray-800">${Number(invoice.amount || 0).toFixed(2)}</p>
          </div>

          <div>
            <label className="text-xs text-gray-500 block mb-1">Fecha Factura</label>
            <p className="text-sm text-gray-800">{new Date(invoice.invoice_date).toLocaleDateString('en-US')}</p>
          </div>

          <div>
            <label className="text-xs text-gray-500 block mb-1">Fecha Servicio</label>
            <p className="text-sm text-gray-800">
              {invoice.service_date ? new Date(invoice.service_date).toLocaleDateString('en-US') : '-'}
            </p>
          </div>

          <div>
            <label className="text-xs text-gray-500 block mb-1">Pendiente</label>
            <p className="text-sm text-purple-700 bg-purple-50 p-2 rounded">
              {invoice.pending_notes || '-'}
            </p>
          </div>
        </div>

        {user?.role === 'admin' && (
          <div className="mt-6 pt-4 border-t border-gray-200 space-y-2">
            <button
              onClick={startEditing}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
            >
              <Edit2 size={16} />
              Editar
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm disabled:opacity-50"
            >
              <Trash2 size={16} />
              Eliminar
            </button>
          </div>
        )}
      </div>

      {/* Right Panel - Contact & Timeline */}
      <div className="w-72 space-y-4 overflow-y-auto">
        {/* Contact Details */}
        <div className="bg-white rounded-xl shadow p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Detalles del Cliente</h3>
            <Link to={`/clients?selected=${invoice.client_id}`} className="text-xs text-blue-600 hover:underline">
              Editar
            </Link>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Building2 size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-800">
                {invoice.company_name || `${invoice.client_first_name} ${invoice.client_last_name}`}
              </p>
              <p className="text-xs text-gray-500">{invoice.plan_name}</p>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <FileText size={14} className="text-gray-400" />
              <span>#{invoice.invoice_number}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar size={14} className="text-gray-400" />
              <span>{new Date(invoice.invoice_date).toLocaleDateString('en-US')}</span>
            </div>
          </div>

          <button className="mt-4 text-xs text-blue-600 hover:underline">
            Ver más info
          </button>
        </div>

        {/* Comments/Timeline */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Comentarios</h3>

          <div className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                placeholder="Agregar comentario..."
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
              >
                <Send size={14} />
              </button>
            </div>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {comments.map((comment) => (
              <div key={comment.id} className="border-l-2 border-blue-200 pl-3">
                <p className="text-xs font-medium text-gray-800">{comment.first_name} {comment.last_name}</p>
                <p className="text-xs text-gray-500 mb-1">
                  {new Date(comment.created_at).toLocaleString('es-ES')}
                </p>
                <p className="text-sm text-gray-700">{comment.comment}</p>
              </div>
            ))}
            {comments.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-2">Sin comentarios</p>
            )}
          </div>
        </div>

        {/* Last Update */}
        <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-500">
          <p className="flex items-center gap-1">
            <Clock size={12} />
            Actualizado: {new Date(invoice.updated_at).toLocaleString('es-ES')}
          </p>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Editar Factura</h2>
              <button onClick={() => setIsEditing(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pendiente</label>
                <textarea
                  value={editData.pending_notes || ''}
                  onChange={(e) => setEditData({ ...editData, pending_notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gestión</label>
                <textarea
                  value={editData.management_notes || ''}
                  onChange={(e) => setEditData({ ...editData, management_notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <select
                  value={editData.status || ''}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value as any })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pendiente</option>
                  <option value="in_process">En Proceso</option>
                  <option value="completed">Completada</option>
                  <option value="denied">Denegada</option>
                  <option value="refacturar">Re-Facturar</option>
                  <option value="paid">Pagada</option>
                </select>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                >
                  {isSaving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={18} />
                  )}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;
